import { DisplayNames, getDisplayName, getSuperRegion } from '@tesla/intl-display-names';
import { parseLocale } from '@tesla/parse-locale';
import { useMemo } from 'react';
/**
 * useProcessedSuperRegions
 * @param locales array of locales to support
 * - will generate data if missing from super regions
 * - will filter out data in super regions if locale is absent
 * @param superRegions array of column data objects representing _unfiltered_ super regions
 * @returns Array of column data objects representing super regions
 */
export const useProcessedSuperRegions = ({ locales, superRegions, }) => {
    // Modify the superRegions data with locales if passed
    const processedSuperRegions = useMemo(() => {
        let filtered = superRegions;
        // Map locales to track which ones have been found in the data
        const localeMap = {};
        locales === null || locales === void 0 ? void 0 : locales.forEach((locale) => {
            localeMap[locale] = false;
        });
        if (locales) {
            // 1. Filter out any authoring data not allowed by the locales array
            filtered = filtered.filter((column) => {
                column.regions = column === null || column === void 0 ? void 0 : column.regions.filter((region) => {
                    region.countries = region === null || region === void 0 ? void 0 : region.countries.filter((country) => {
                        country.languages = country === null || country === void 0 ? void 0 : country.languages.filter((lang) => {
                            const { country: countryCode, lang: langCode } = parseLocale((lang === null || lang === void 0 ? void 0 : lang.localeCode) || 'en-US');
                            const normalizedLocaleCode = `${langCode.toLowerCase()}-${countryCode.toUpperCase()}`;
                            // If found, mark this locale to skip it in the generation phase
                            const found = locales.includes(normalizedLocaleCode);
                            if (found) {
                                localeMap[normalizedLocaleCode] = true;
                            }
                            // Remove any languages that don't appear in the locales array
                            return found;
                        });
                        // Remove empty countries/regions back up the stack
                        return country.languages.length > 0;
                    });
                    return region.countries.length > 0;
                });
                return column.regions.length > 0;
            });
            // 2. Generate data from locale code if any locales are missing from the authoring data
            // - check for DisplayNames' base class to prevent crashes in older browsers
            if (Intl === null || Intl === void 0 ? void 0 : Intl.DisplayNames) {
                const enLangs = new DisplayNames('en', { type: 'language' });
                const enCountries = new DisplayNames('en', { type: 'region' });
                Object.entries(localeMap).forEach(([locale, found]) => {
                    var _a, _b;
                    if (!found) {
                        const { country: countryCode, lang: langCode } = parseLocale(locale);
                        const superRegion = getSuperRegion(countryCode, langCode);
                        let regionMatch = false;
                        let countryMatch = false;
                        let langMatch = false;
                        // Defined as functions so that they only generate when needed
                        const generateLang = () => ({
                            adminName: enLangs.of(langCode),
                            languageCode: langCode,
                            languageName: String(getDisplayName(langCode, locale, { type: 'language' })),
                            localeCode: locale,
                        });
                        const generateCountry = () => ({
                            adminName: enCountries.of(countryCode),
                            countryCode: countryCode,
                            countryName: String(getDisplayName(countryCode, locale, { type: 'region' })),
                            languages: [generateLang()],
                        });
                        const generateRegion = () => ({
                            countries: [generateCountry()],
                            regionName: (superRegion === null || superRegion === void 0 ? void 0 : superRegion.label) || '',
                        });
                        // Look for authoring data that matches this locale
                        // If data is missing, generate it at the lowest level possible to avoid duplicates
                        for (const [i, column] of filtered.entries()) {
                            // No way to identify whether a country should check a specific column or not
                            if (column.regions) {
                                for (const region of column.regions) {
                                    if (region.countries && region.regionName === (superRegion === null || superRegion === void 0 ? void 0 : superRegion.label)) {
                                        regionMatch = true;
                                        for (const country of region.countries) {
                                            if (country.languages &&
                                                ((_a = country.countryCode) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === countryCode.toLowerCase()) {
                                                countryMatch = true;
                                                for (const lang of country.languages) {
                                                    if (((_b = lang.languageCode) === null || _b === void 0 ? void 0 : _b.toLowerCase()) === langCode.toLowerCase()) {
                                                        langMatch = true;
                                                        break;
                                                    }
                                                }
                                                if (!langMatch) {
                                                    country.languages.push(generateLang());
                                                }
                                                break;
                                            }
                                        }
                                        if (!countryMatch) {
                                            region.countries.push(generateCountry());
                                        }
                                        break;
                                    }
                                }
                            }
                            // Make sure any completely new countries are added only in the last column
                            // to avoid duplicates, since there's no distinguishing id for columns
                            if (column.regions && !regionMatch && i === filtered.length - 1) {
                                column.regions.push(generateRegion());
                            }
                        }
                    }
                });
            }
        }
        return filtered;
    }, [locales, superRegions]);
    return processedSuperRegions;
};
